/* Navigation – Gold + Olive Design
 * Sticky navbar, transparent → solid on scroll
 * Gold logo, olive CTA, gold accents
 * Mobile: fullscreen overlay menu for guaranteed visibility of all items
 */
import { useState, useEffect } from "react";
import { IMAGES, NAV_ITEMS } from "@/lib/constants";
import { Menu, X } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

export default function Navigation() {
  const { t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled || mobileOpen
            ? "bg-[oklch(0.18_0.02_280/0.95)] backdrop-blur-md shadow-lg py-3"
            : "bg-transparent py-5"
        }`}
      >
        <div className="container flex items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex items-center gap-3 group">
            <img
              src={IMAGES.logo}
              alt="AYU Logo"
              className="h-11 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </a>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-3 xl:gap-6">
            {NAV_ITEMS.map(item => (
              <a
                key={item.href}
                href={item.href}
                className="whitespace-nowrap text-[11px] xl:text-sm font-medium text-[oklch(0.85_0.01_80)] hover:text-[oklch(0.78_0.11_85)] transition-colors duration-300 tracking-wide"
              >
                {t(item.labelKey as any)}
              </a>
            ))}
            <a
              href="/funnel"
              className="btn-olive whitespace-nowrap px-4 py-2 xl:px-6 xl:py-2.5 rounded-full text-[11px] xl:text-sm font-semibold tracking-wide shrink-0"
            >
              {t("landing.nav.jetztStarten")}
            </a>
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden text-[oklch(0.85_0.01_80)] p-2 z-[60]"
            aria-label={t("landing.nav.jetztStarten")}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Fullscreen Menu Overlay */}
      <div
        className={`lg:hidden fixed inset-0 z-40 bg-[oklch(0.14_0.02_280/0.98)] backdrop-blur-md transition-all duration-300 ${
          mobileOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="flex flex-col items-center justify-center min-h-screen py-24 px-6 overflow-y-auto">
          <div className="flex flex-col items-center gap-5 w-full max-w-full sm:max-w-md">
            {NAV_ITEMS.map(item => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setMobileOpen(false)}
                className="text-[oklch(0.85_0.01_80)] hover:text-[oklch(0.78_0.11_85)] transition-colors py-2 text-xl font-medium text-center"
              >
                {t(item.labelKey as any)}
              </a>
            ))}
            <a
              href="/funnel"
              onClick={() => setMobileOpen(false)}
              className="btn-olive px-8 py-3.5 rounded-full text-center font-semibold mt-4 w-full max-w-full text-lg sm:w-auto"
            >
              {t("landing.nav.jetztStarten")}
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
